;(() => {
  const namespace = (globalThis.StylistLocator = globalThis.StylistLocator || {})

  // ============================================================================
  // Image Modal
  // ============================================================================

  let modalState = {
    modal: null,
    images: [],
    currentIndex: 0,
    keydownHandler: null,
  }

  /**
   * Create or get the image preview modal element.
   */
  function getImageModalElement() {
    if (!modalState.modal) {
      const modal = document.createElement('div')
      modal.className = 'stylist-locator__image-modal'
      modal.innerHTML = `
        <div class="stylist-locator__image-modal-content">
          <button class="stylist-locator__image-modal-close" type="button" aria-label="Close image preview">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M17 1L1 17M1 1L17 17" stroke="black" stroke-opacity="0.6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button class="stylist-locator__image-modal-nav stylist-locator__image-modal-nav--prev" type="button" aria-label="Previous image">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
          <button class="stylist-locator__image-modal-nav stylist-locator__image-modal-nav--next" type="button" aria-label="Next image">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
          <img class="stylist-locator__image-modal-img" src="" alt="">
        </div>
      `
      document.body.appendChild(modal)
      modalState.modal = modal
    }
    return modalState.modal
  }

  function getModalElements(modal) {
    return {
      img: modal.querySelector('.stylist-locator__image-modal-img'),
      closeButton: modal.querySelector('.stylist-locator__image-modal-close'),
      prevButton: modal.querySelector('.stylist-locator__image-modal-nav--prev'),
      nextButton: modal.querySelector('.stylist-locator__image-modal-nav--next'),
    }
  }

  function setBodyScrollLocked(locked) {
    document.body.style.overflow = locked ? 'hidden' : ''
  }

  /**
   * Update modal image and button states.
   */
  function updateModalImage() {
    const modal = modalState.modal
    if (!modal) return

    const { img, prevButton, nextButton } = getModalElements(modal)

    if (img && modalState.images[modalState.currentIndex]) {
      img.src = modalState.images[modalState.currentIndex]
    }
    if (prevButton) prevButton.disabled = modalState.currentIndex === 0
    if (nextButton) nextButton.disabled = modalState.currentIndex === modalState.images.length - 1
  }

  /**
   * Close the image modal.
   */
  function closeImageModal() {
    const modal = modalState.modal
    if (!modal) return

    modal.classList.remove('stylist-locator__image-modal--open')
    setBodyScrollLocked(false)

    if (modalState.keydownHandler) {
      document.removeEventListener('keydown', modalState.keydownHandler)
      modalState.keydownHandler = null
    }
  }

  /**
   * Navigate to next image.
   */
  function showNextImage() {
    if (modalState.currentIndex < modalState.images.length - 1) {
      modalState.currentIndex++
      updateModalImage()
    }
  }

  /**
   * Navigate to previous image.
   */
  function showPrevImage() {
    if (modalState.currentIndex > 0) {
      modalState.currentIndex--
      updateModalImage()
    }
  }

  /**
   * Handle keyboard navigation.
   */
  function handleModalKeydown(e) {
    if (!modalState.modal?.classList.contains('stylist-locator__image-modal--open')) return

    if (e.key === 'Escape') closeImageModal()
    else if (e.key === 'ArrowRight') showNextImage()
    else if (e.key === 'ArrowLeft') showPrevImage()
  }

  /**
   * Show image modal with given images and starting index.
   * @param {Array<string>} images - Array of image URLs
   * @param {number} startIndex - Starting image index
   */
  function showImageModal(images, startIndex = 0) {
    if (!images || images.length === 0) return

    const modal = getImageModalElement()
    const { closeButton, prevButton, nextButton } = getModalElements(modal)

    // Update state
    modalState.images = images
    modalState.currentIndex = Math.max(0, Math.min(startIndex, images.length - 1))

    // Setup event listeners
    closeButton.onclick = closeImageModal
    prevButton.onclick = showPrevImage
    nextButton.onclick = showNextImage
    modal.onclick = (e) => {
      if (e.target === modal) closeImageModal()
    }

    // Keyboard navigation
    if (modalState.keydownHandler) {
      document.removeEventListener('keydown', modalState.keydownHandler)
    }
    modalState.keydownHandler = handleModalKeydown
    document.addEventListener('keydown', modalState.keydownHandler)

    // Show modal
    updateModalImage()
    modal.classList.add('stylist-locator__image-modal--open')
    setBodyScrollLocked(true)
  }

  // ============================================================================
  // Gallery Initialization
  // ============================================================================

  /**
   * Check if gallery is in a drawer (uses native scrolling).
   */
  function isDrawerGallery(gallery) {
    return Boolean(gallery.closest('.stylist-locator__drawer'))
  }

  /**
   * Collect image URLs from gallery slides.
   */
  function collectGalleryImages(slides) {
    return slides
      .map(slide => slide.dataset.imageSrc)
      .filter(Boolean)
  }

  /**
   * Setup click handlers for gallery slides to open modal.
   */
  function setupGalleryModalHandlers(gallery, slides, images) {
    if (images.length === 0) return

    slides.forEach((slide, index) => {
      slide.style.cursor = 'pointer'
      slide.addEventListener('click', () => {
        showImageModal(images, index)
      })
    })
  }

  /**
   * Initialize carousel gallery (desktop/list view).
   */
  function initializeCarouselGallery(gallery, track, slides, prevButton, nextButton, viewport) {
    const visibleCount = Number(gallery.dataset.galleryVisibleCount || '1') || 1
    const totalSlides = slides.length
    const maxIndex = Math.max(0, totalSlides - visibleCount)

    track.style.setProperty('--gallery-visible-count', String(visibleCount))
    gallery.classList.toggle(
      'stylist-locator__gallery--has-controls',
      Boolean(prevButton && nextButton && totalSlides > visibleCount)
    )

    let index = 0

    const updateButtonState = () => {
      if (prevButton) prevButton.disabled = index === 0
      if (nextButton) nextButton.disabled = index === maxIndex
    }

    const applyTransform = (instant = false) => {
      const currentSlide = slides[index]
      if (!currentSlide) return

      const offset = currentSlide.offsetLeft
      if (instant) {
        const prevTransition = track.style.transition
        track.style.transition = 'none'
        track.style.transform = `translate3d(-${offset}px, 0, 0)`
        void track.offsetWidth // Force reflow
        track.style.transition = prevTransition || ''
      } else {
        track.style.transform = `translate3d(-${offset}px, 0, 0)`
      }
      updateButtonState()
    }

    const goTo = (nextIndex, { instant = false } = {}) => {
      const target = Math.max(0, Math.min(nextIndex, maxIndex))
      if (target === index && !instant) return
      index = target
      applyTransform(instant)
    }

    if (prevButton) {
      prevButton.addEventListener('click', () => goTo(index - 1))
    }
    if (nextButton) {
      nextButton.addEventListener('click', () => goTo(index + 1))
    }

    // Handle resize
    if (typeof globalThis.ResizeObserver === 'function' && viewport) {
      const resizeObserver = new globalThis.ResizeObserver(() => goTo(index, { instant: true }))
      resizeObserver.observe(viewport)
      gallery._galleryResizeObserver = resizeObserver

      const cleanup = () => {
        resizeObserver.disconnect()
        gallery._galleryResizeObserver = null
      }

      // Watch for gallery removal from DOM
      if (typeof globalThis.MutationObserver === 'function') {
        const removalObserver = new globalThis.MutationObserver(() => {
          if (!gallery.isConnected) cleanup()
        })
        const target = gallery.parentNode || document.body
        removalObserver.observe(target, { childList: true, subtree: true })
      }

      return cleanup
    }

    goTo(0, { instant: true })
    return null
  }

  /**
   * Initialize image galleries within a scope.
   * @param {HTMLElement} scope - Container to search for galleries
   * @returns {Set<Function>} Set of cleanup functions
   */
  function initializeGalleries(scope) {
    if (!scope) return new Set()

    const galleries = scope.querySelectorAll('[data-gallery-root]')
    const cleanups = new Set()

    galleries.forEach((gallery) => {
      if (gallery._galleryInitialized) return

      const track = gallery.querySelector('[data-gallery-track]')
      const slides = track ? Array.from(track.querySelectorAll('[data-gallery-slide]')) : []
      if (!track || slides.length === 0) return

      // Collect images and setup modal handlers
      const images = collectGalleryImages(slides)
      setupGalleryModalHandlers(gallery, slides, images)

      // Drawer galleries use native scrolling (handled by CSS)
      if (isDrawerGallery(gallery)) {
        gallery._galleryInitialized = true
        return
      }

      // Carousel galleries need JavaScript controls
      const prevButton = gallery.querySelector('[data-gallery-prev]')
      const nextButton = gallery.querySelector('[data-gallery-next]')
      const viewport = gallery.querySelector('[data-gallery-viewport]')

      const cleanup = initializeCarouselGallery(gallery, track, slides, prevButton, nextButton, viewport)
      if (cleanup) cleanups.add(cleanup)

      gallery._galleryInitialized = true
    })

    return cleanups
  }

  // ============================================================================
  // Exports
  // ============================================================================

  namespace.gallery = Object.assign({}, namespace.gallery, {
    initializeGalleries,
    showImageModal,
  })
})()
